import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getCategories, deleteCategory, bulkDeleteCategories } from '../../../services/categoryService';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiCheck, FiX, FiDownload } from 'react-icons/fi';
import { exportToCSV, categoryColumns } from '../../../utils/csvExport';

const CategoriesPage = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await getCategories();
            setCategories(response.data || []);
            // Reset selections when fetching new data
            setSelectedCategories([]);
            setSelectAll(false);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!categoryToDelete) return;

        try {
            await deleteCategory(categoryToDelete._id);
            toast.success(`Category "${categoryToDelete.name}" deleted successfully`);
            setCategories(categories.filter(cat => cat._id !== categoryToDelete._id));
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error(error.response?.data?.message || 'Failed to delete category');
        } finally {
            setShowDeleteModal(false);
            setCategoryToDelete(null);
        }
    };

    const handleCheckboxChange = (categoryId) => {
        setSelectedCategories(prev => {
            if (prev.includes(categoryId)) {
                return prev.filter(id => id !== categoryId);
            } else {
                return [...prev, categoryId];
            }
        });
    };

    const handleSelectAllChange = () => {
        if (selectAll) {
            setSelectedCategories([]);
        } else {
            setSelectedCategories(categories.map(category => category._id));
        }
        setSelectAll(!selectAll);
    };

    const handleBulkDeleteClick = () => {
        if (selectedCategories.length === 0) {
            toast.info('No categories selected');
            return;
        }
        setShowBulkDeleteModal(true);
    };

    const handleBulkDeleteConfirm = async () => {
        if (selectedCategories.length === 0) return;

        try {
            // Use the bulkDeleteCategories function instead of individual deleteCategory calls
            const result = await bulkDeleteCategories(selectedCategories);

            // Show appropriate messages based on the response
            if (result.notFound && result.notFound.length > 0) {
                toast.warning(`${result.notFound.length} categories were not found`);
            }

            if (result.deletedCount > 0) {
                toast.success(`${result.deletedCount} categories deleted successfully`);
            } else {
                toast.error('No categories were deleted');
            }

            // Refresh the list
            fetchCategories();
        } catch (error) {
            console.error('Error in bulk delete operation:', error);
            toast.error(error.response?.data?.message || 'Failed to complete bulk delete operation');
        } finally {
            setShowBulkDeleteModal(false);
            setSelectedCategories([]);
            setSelectAll(false);
        }
    };

    const handleExportCSV = () => {
        if (categories.length === 0) {
            toast.info('No categories to export');
            return;
        }

        try {
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `categories_export_${timestamp}.csv`;
            exportToCSV(categories, categoryColumns, filename);
            toast.success('Categories exported successfully');
        } catch (error) {
            console.error('Error exporting categories:', error);
            toast.error('Failed to export categories');
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Categories</h1>
                <div className="flex space-x-2">
                    {selectedCategories.length > 0 && (
                        <button
                            onClick={handleBulkDeleteClick}
                            className="btn btn-outline border-red-300 text-red-600 hover:bg-red-50 flex items-center"
                        >
                            <FiTrash2 className="mr-2" /> Delete Selected ({selectedCategories.length})
                        </button>
                    )}
                    <button
                        onClick={handleExportCSV}
                        className="btn btn-outline border-green-300 text-green-600 hover:bg-green-50 flex items-center"
                    >
                        <FiDownload className="mr-2" /> Export CSV
                    </button>
                    <Link to="/admin/categories/new" className="btn btn-primary flex items-center">
                        <FiPlus className="mr-2" /> Add Category
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden">
                    {categories.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            No categories found. Click the "Add Category" button to create one.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-3 text-left">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                checked={selectAll}
                                                onChange={handleSelectAllChange}
                                            />
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                {categories.map((category) => (
                                    <tr key={category._id} className={`hover:bg-gray-50 ${selectedCategories.includes(category._id) ? 'bg-blue-50' : ''}`}>
                                        <td className="px-3 py-4">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                    checked={selectedCategories.includes(category._id)}
                                                    onChange={() => handleCheckboxChange(category._id)}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                {category.imageUrl && (
                                                    <div className="flex-shrink-0 h-10 w-10 mr-3">
                                                        <img
                                                            className="h-10 w-10 rounded-md object-cover"
                                                            src={category.imageUrl}
                                                            alt={category.name}
                                                        />
                                                    </div>
                                                )}
                                                <div className="text-sm font-medium text-gray-900">{category.name}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-500 truncate max-w-xs">
                                                {category.description}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${category.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                                }`}>
                                                {category.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-3">
                                                <Link
                                                    to={`/admin/categories/${category._id}/view`}
                                                    className="text-gray-600 hover:text-primary-600"
                                                >
                                                    <FiEye size={18} />
                                                </Link>
                                                <Link
                                                    to={`/admin/categories/${category._id}/edit`}
                                                    className="text-gray-600 hover:text-primary-600"
                                                >
                                                    <FiEdit size={18} />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteClick(category)}
                                                    className="text-gray-600 hover:text-primary-600"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Single Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-primary-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Category</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete the category "{categoryToDelete?.name}"? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleDeleteConfirm}
                                >
                                    Delete
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-[#f8f8f8] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-[#f8f8f8] px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <FiTrash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Selected Categories</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete {selectedCategories.length} selected categories? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleBulkDeleteConfirm}
                                >
                                    Delete All Selected
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-[#f8f8f8] text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setShowBulkDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default CategoriesPage;